import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "../../components/ui-extras";
import { ArrowsClockwise, FileText, Warning } from "@phosphor-icons/react";

const STATUS_VARIANT = {
  ok: "success",
  partial: "warning",
  failed: "error",
  in_progress: "info",
  empty: "info",
  missing: "error",
};

export default function PdfExtractions() {
  const [uploads, setUploads] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // { upload, pages, busy }

  const load = async () => {
    setLoading(true);
    try {
      const [uploadsRes, jobsRes] = await Promise.all([
        api.get("/pdf-uploads"),
        api.get("/jobs", { params: { kind: "pdf_extraction", status: "running", limit: 20 } }),
      ]);
      setUploads(uploadsRes.data.uploads || []);
      setActiveJobs(jobsRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load PDF uploads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Phase 2.E enhancement: poll for in-progress extractions every 3s.
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/jobs", {
          params: { kind: "pdf_extraction", status: "running", limit: 20 },
        });
        setActiveJobs(data || []);
      } catch { /* silent — background refresh */ }
    }, 3000);
    return () => clearInterval(t);
  }, []);

  const openDrill = async (u) => {
    setDrill({ upload: u, pages: [], busy: true });
    try {
      const { data } = await api.get(`/pdf-uploads/${u.upload_id}/pages`);
      setDrill({ upload: data.upload, pages: data.pages || [], busy: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load page details");
      setDrill(null);
    }
  };

  const retryFailed = async () => {
    if (!drill?.upload?.upload_id) return;
    setDrill((d) => ({ ...d, busy: true }));
    try {
      const { data } = await api.post(`/pdf-uploads/${drill.upload.upload_id}/retry-failed`);
      toast.success(`Retried ${data.retried} page(s) · ${data.pages_failed} still failed`);
      await openDrill(drill.upload);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
      setDrill((d) => ({ ...d, busy: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="pdf-extractions-page">
      <PageHeader
        title="PDF Extractions"
        subtitle="Page-by-page extraction history. Drill into a row to inspect per-page status and retry failed pages."
        actions={
          <button className="gf-btn gf-btn-outline" onClick={load} data-testid="refresh-pdf-uploads">
            <ArrowsClockwise size={14}/> Refresh
          </button>
        }
      />

      {/* ---- IN-PROGRESS JOBS PANEL ---- */}
      {activeJobs.length > 0 && (
        <div className="gf-card" data-testid="pdf-active-jobs-panel">
          <div className="gf-card-header">
            <h3>In progress ({activeJobs.length})</h3>
            <div className="text-xs text-zinc-500 mt-1">Auto-refresh every 3 s.</div>
          </div>
          <div className="space-y-3 p-4">
            {activeJobs.map((j) => {
              const c = j.counters || {};
              const pct = Math.max(0, Math.min(100, j.progress_percent || 0));
              return (
                <div key={j.job_id} className="border border-zinc-200 p-3 text-xs" data-testid={`pdf-active-job-${j.job_id}`}>
                  <div className="flex justify-between items-center mb-1">
                    <div className="font-mono">{j.job_id}</div>
                    <div className="text-zinc-500">{j.current_step || "—"}</div>
                  </div>
                  <div className="w-full bg-zinc-100 h-2 mb-2 overflow-hidden">
                    <div
                      className="bg-emerald-600 h-2 transition-all"
                      style={{ width: `${pct}%` }}
                      data-testid={`pdf-active-job-progress-${j.job_id}`}
                    />
                  </div>
                  <div className="flex justify-between text-zinc-600">
                    <span>{pct}% · page {c.current_page || 0} / {c.total_pages || "?"}</span>
                    <span>started {j.started_at?.slice(11, 19) || ""}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="gf-card">
        <div className="gf-card-header"><h3>Recent PDF uploads ({uploads.length})</h3></div>
        <div className="overflow-auto">
          <table className="gf-table" data-testid="pdf-uploads-table">
            <thead>
              <tr>
                <th>CREATED</th>
                <th>FILE</th>
                <th>STATUS</th>
                <th className="text-right">PAGES</th>
                <th className="text-right">OK</th>
                <th className="text-right">FAILED</th>
                <th className="text-right">EMPTY</th>
                <th className="text-right">ELAPSED</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center text-zinc-400 py-8">Loading…</td></tr>}
              {!loading && uploads.length === 0 && (
                <tr><td colSpan={9} className="text-center text-zinc-400 py-8">No PDF extractions yet.</td></tr>
              )}
              {uploads.map((u) => (
                <tr key={u.upload_id} data-testid={`pdf-upload-row-${u.upload_id}`}>
                  <td className="font-mono text-xs">{u.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="text-xs max-w-[280px] truncate" title={u.filename}>
                    <FileText size={12} style={{ display: "inline", marginRight: 6 }}/>{u.filename || u.upload_id}
                  </td>
                  <td>
                    <Badge variant={STATUS_VARIANT[u.status] || "info"}>{u.status || "—"}</Badge>
                  </td>
                  <td className="gf-num">{u.total_pages || "—"}</td>
                  <td className="gf-num">{u.pages_ok ?? "—"}</td>
                  <td className="gf-num">
                    {(u.pages_failed || 0) > 0 ? <Badge variant="error">{u.pages_failed}</Badge> : "—"}
                  </td>
                  <td className="gf-num">{u.pages_empty ?? "—"}</td>
                  <td className="gf-num font-mono text-xs">{u.elapsed_ms ? `${u.elapsed_ms} ms` : "—"}</td>
                  <td className="text-right">
                    <button
                      className="gf-btn gf-btn-ghost"
                      onClick={() => openDrill(u)}
                      data-testid={`drill-pdf-upload-${u.upload_id}`}
                    >
                      Inspect
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- DRILL-DOWN MODAL ---- */}
      {drill && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDrill(null)}>
          <div className="bg-white w-full max-w-5xl border border-zinc-200 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pdf-drill-modal">
            <div className="gf-card-header flex items-start justify-between">
              <div>
                <h3>{drill.upload?.filename || drill.upload?.upload_id}</h3>
                <div className="text-xs text-zinc-500 mt-1">
                  {drill.upload?.total_pages || 0} pages ·{" "}
                  <Badge variant={STATUS_VARIANT[drill.upload?.status] || "info"}>{drill.upload?.status}</Badge>{" "}
                  · {drill.upload?.pages_ok ?? 0} ok · {drill.upload?.pages_failed ?? 0} failed
                  {(drill.upload?.missing_pages || []).length > 0 && (
                    <> · missing: {(drill.upload.missing_pages || []).join(", ")}</>
                  )}
                </div>
              </div>
              {(drill.upload?.pages_failed || 0) > 0 && (
                <button
                  className="gf-btn gf-btn-primary"
                  disabled={drill.busy}
                  onClick={retryFailed}
                  data-testid="retry-failed-pdf-pages"
                >
                  <ArrowsClockwise size={14}/> Retry failed pages
                </button>
              )}
            </div>
            <div className="overflow-auto flex-1">
              <table className="gf-table" data-testid="pdf-pages-table">
                <thead>
                  <tr>
                    <th className="text-right">PAGE #</th>
                    <th>STATUS</th>
                    <th className="text-right">CHARS</th>
                    <th className="text-right">RETRIES</th>
                    <th>EXTRACTED AT</th>
                    <th>ERROR</th>
                  </tr>
                </thead>
                <tbody>
                  {drill.busy && <tr><td colSpan={6} className="text-center text-zinc-400 py-8">Loading…</td></tr>}
                  {!drill.busy && drill.pages.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-zinc-400 py-8">No page records yet.</td></tr>
                  )}
                  {drill.pages.map((p) => (
                    <tr key={p.page_number} data-testid={`pdf-page-row-${p.page_number}`}>
                      <td className="gf-num">{p.page_number}</td>
                      <td>
                        <Badge variant={STATUS_VARIANT[p.extraction_status] || "info"}>
                          {p.extraction_status}
                        </Badge>
                      </td>
                      <td className="gf-num">{p.text_length ?? "—"}</td>
                      <td className="gf-num">{p.retry_count ?? 0}</td>
                      <td className="font-mono text-xs">{p.extracted_at?.slice(0, 19).replace("T", " ")}</td>
                      <td className="text-xs text-red-700 max-w-[400px] truncate" title={p.error || ""}>
                        {p.error ? <><Warning size={12} style={{ display: "inline", marginRight: 4 }}/>{p.error}</> : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setDrill(null)} data-testid="close-pdf-drill-modal">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
